import { toast } from 'react-toastify'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams } from 'src/mentor/services'

export async function getSectionsAndQuestionsBasedOnFilledAnswers({ 
  sections, 
  answers, 
  fetchSectionsIfMissing = false, 
  doctorSpecializations = null, 
  visitType = null,
  useDebugNumbering = false, // Only use "Question 1", "Question 2" for debugging in history drawer
  screeningForSpecializations = null, // Specializations saved with the report (for history drawer)
}) {
  
  const validAnswers = answers?.filter((answer) => !!answer.response)
  
  // If no valid answers, return empty
  if (!validAnswers || validAnswers.length === 0) {
    return []
  }
  
  // If we have answers but no sections AND no specializations, we can't fetch sections
  // This is a problem - we need specializations to fetch sections
  if ((!sections || sections.length === 0) && fetchSectionsIfMissing && (!doctorSpecializations || doctorSpecializations.length === 0)) {
    if (useDebugNumbering) {
      console.warn('[History Drawer - getSectionsAndQuestionsBasedOnFilledAnswers] WARNING: Cannot fetch sections - doctorSpecializations is empty!', {
        hasAnswers: validAnswers.length > 0,
        hasSections: sections && sections.length > 0,
        fetchSectionsIfMissing,
        doctorSpecializations,
      })
    }
    // Still try to reconstruct from answers even without sections
  }
  
  // Helper to normalize IDs for comparison (handle string/number mismatches, trim whitespace, and case)
  const normalizeId = (id) => {
    if (id == null || id === undefined) return null
    // Convert to string, trim whitespace
    const normalized = String(id).trim()
    return normalized || null
  }
  
  // Priority order for resolving questions and sections:
  // 1. questionText and sectionText from saved answers (already in answers)
  // 2. Use passed sections from parent (screening report making page)
  // 3. Fetch using screeningForSpecializations from saved report
  // 4. Fetch using doctorSpecializations from props/report
  // 5. Reconstruct from answers as last resort
  
  // If sections are missing and we should fetch them, try to get from API
  // Only fetch if sections are completely missing (not passed from parent)
  // If sections are provided (even if they don't match), use them and don't fetch
  let effectiveSections = sections
  let shouldFetchSections = false
  
  // Determine which specializations to use for fetching (prioritize screeningForSpecializations from saved report)
  const effectiveSpecializationsForFetch = 
    screeningForSpecializations ||  // First priority: from saved report
    doctorSpecializations ||        // Second: from props
    ['general']                     // Fallback
  
  
  // Only fetch if sections are completely missing AND fetchSectionsIfMissing is true
  if ((!sections || sections.length === 0) && fetchSectionsIfMissing) {
    shouldFetchSections = true
  }
  
  // If we have sections, try to match them with answers first
  if (effectiveSections && Array.isArray(effectiveSections) && effectiveSections.length > 0) {
    const sectionsWithValidQuestions = effectiveSections?.map((section) => {
      const validQuestions = section?.questions?.filter((question) => {
        const questionId = normalizeId(question.id)
        if (!questionId) return false
        return validAnswers?.some((answer) => {
          const answerQuestionId = normalizeId(answer?.questionId)
          return answerQuestionId === questionId && answerQuestionId !== null
        })
      })
      return {
        ...section,
        questions: validQuestions,
      }
    })
    // DEBUG: Track global question number across all sections
    let globalQuestionNumber = 0
    
    const matchedSections = sectionsWithValidQuestions
      ?.filter((section) => section?.questions?.length > 0)
      ?.map((section) => {
        // Try to get section title from answers if available (for old reports that might have sectionText/sectionTitle)
        const firstQuestion = section?.questions?.[0]
        const firstAnswer = firstQuestion ? validAnswers?.find((answer) => {
          const answerQuestionId = normalizeId(answer?.questionId)
          const questionId = normalizeId(firstQuestion?.id)
          return answerQuestionId === questionId && answerQuestionId !== null
        }) : null
        
        // Prioritize sectionText/sectionTitle from answer, then use section's title, finally sectionId
        const sectionTitle = firstAnswer?.sectionText || 
                            firstAnswer?.sectionTitle || 
                            section?.title ||
                            section?.id
        
        return {
          ...section,
          title: sectionTitle, // Ensure we use the best available title
          questions: section?.questions?.map((question) => {
            globalQuestionNumber++ // Increment global counter for each question
            const questionId = normalizeId(question?.id)
            const answer = validAnswers?.find((answer) => {
              const answerQuestionId = normalizeId(answer?.questionId)
              return answerQuestionId === questionId && answerQuestionId !== null
            })
            return {
              ...question,
              // Use debug numbering only if requested (for history drawer), otherwise use actual question text
              questionText: useDebugNumbering 
                ? `Question ${globalQuestionNumber}`
                : (answer?.questionText || question?.questionText),
              shortQuestionText: useDebugNumbering
                ? `Question ${globalQuestionNumber}`
                : (answer?.shortQuestionText || question?.shortQuestionText || question?.questionText),
              translations: answer?.translations || question?.translations || {},
              answer,
            }
          }),
        }
      })
    
    // If sections matched answers, return them
    if (matchedSections && matchedSections.length > 0) {
      return matchedSections
    }
    
    // If sections were provided but didn't match any answers, DON'T fetch
    // The sections were passed from the parent (screening report making page)
    // Use them even if they don't match - they'll be used for question text lookup
    // Only fetch if sections were completely missing (handled above)
    // But we still need to reconstruct from answers to show the questions
    // Fall through to reconstruction logic below
  }
  
  // Fetch sections from API if needed (same mechanism as initial load)
  // Use a static cache to prevent duplicate fetches for the same parameters
  const fetchCacheKey = `${(effectiveSpecializationsForFetch || ['general']).join(',')}-${visitType || 'new'}`
  if (shouldFetchSections && !getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache?.[fetchCacheKey]) {
    try {
      // Mark as fetching to prevent concurrent calls
      if (!getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache) {
        getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache = {}
      }
      getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache[fetchCacheKey] = 'fetching'
      
      // Use effectiveSpecializationsForFetch (prioritizes screeningForSpecializations from saved report)
      const fetchedSections = await getScreeningSectionsFromDb({ 
        doctorSpecializations: effectiveSpecializationsForFetch, 
        visitType: visitType || 'new' 
      })
      
      // Cache the result (with a timeout to allow refetching after some time)
      if (fetchedSections && fetchedSections.length > 0) {
        getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache[fetchCacheKey] = fetchedSections
        // Clear cache after 5 minutes to allow refetching if needed
        setTimeout(() => {
          delete getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache[fetchCacheKey]
        }, 5 * 60 * 1000)
        effectiveSections = fetchedSections
        
        // Try to match fetched sections with answers
        const sectionsWithValidQuestions = effectiveSections?.map((section) => {
          const validQuestions = section?.questions?.filter((question) => {
            const questionId = normalizeId(question.id)
            if (!questionId) return false
            return validAnswers?.some((answer) => {
              const answerQuestionId = normalizeId(answer?.questionId)
              return answerQuestionId === questionId && answerQuestionId !== null
            })
          })
          return {
            ...section,
            questions: validQuestions,
          }
        })
        const matchedSections = sectionsWithValidQuestions
          ?.filter((section) => section?.questions?.length > 0)
          ?.map((section) => {
            // Try to get section title from answers if available (for old reports that might have sectionText/sectionTitle)
            const firstQuestion = section?.questions?.[0]
            const firstAnswer = firstQuestion ? validAnswers?.find((answer) => {
              const answerQuestionId = normalizeId(answer?.questionId)
              const questionId = normalizeId(firstQuestion?.id)
              return answerQuestionId === questionId && answerQuestionId !== null
            }) : null
            
            // Prioritize sectionText/sectionTitle from answer, then use section's title, finally sectionId
            const sectionTitle = firstAnswer?.sectionText || 
                                firstAnswer?.sectionTitle || 
                                section?.title ||
                                section?.id
            
            return {
              ...section,
              title: sectionTitle, // Ensure we use the best available title
              questions: section?.questions?.map((question) => {
                const questionId = normalizeId(question?.id)
                const answer = validAnswers?.find((answer) => {
                  const answerQuestionId = normalizeId(answer?.questionId)
                  return answerQuestionId === questionId && answerQuestionId !== null
                })
                return {
                  ...question,
                  // Prioritize questionText from answer if available (for past reports), otherwise use question's text
                  questionText: answer?.questionText || question?.questionText,
                  shortQuestionText: answer?.shortQuestionText || question?.shortQuestionText || question?.questionText,
                  translations: answer?.translations || question?.translations || {},
                  answer,
                }
              }),
            }
          })
        
        // If fetched sections matched answers, return them
        if (matchedSections && matchedSections.length > 0) {
          return matchedSections
        }
      }
    } catch (error) {
      // Clear the fetching flag on error
      if (getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache?.[fetchCacheKey] === 'fetching') {
        delete getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache[fetchCacheKey]
      }
      // Silently fail - will use reconstruction from answers
    }
  } else if (shouldFetchSections && getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache?.[fetchCacheKey]) {
    // Use cached result if available (waiting for fetch to complete)
    const cachedSections = getSectionsAndQuestionsBasedOnFilledAnswers._fetchCache[fetchCacheKey]
    if (cachedSections && cachedSections !== 'fetching' && Array.isArray(cachedSections) && cachedSections.length > 0) {
      effectiveSections = cachedSections
      
      // Try to match cached sections with answers
      const sectionsWithValidQuestions = effectiveSections?.map((section) => {
        const validQuestions = section?.questions?.filter((question) => {
          const questionId = normalizeId(question.id)
          if (!questionId) return false
          return validAnswers?.some((answer) => {
            const answerQuestionId = normalizeId(answer?.questionId)
            return answerQuestionId === questionId && answerQuestionId !== null
          })
        })
        return {
          ...section,
          questions: validQuestions,
        }
      })
      // DEBUG: Track global question number for cached sections too
      let globalQuestionNumber = 0
      
      const matchedSections = sectionsWithValidQuestions
        ?.filter((section) => section?.questions?.length > 0)
        ?.map((section) => {
          const firstQuestion = section?.questions?.[0]
          const firstAnswer = firstQuestion ? validAnswers?.find((answer) => {
            const answerQuestionId = normalizeId(answer?.questionId)
            const questionId = normalizeId(firstQuestion?.id)
            return answerQuestionId === questionId && answerQuestionId !== null
          }) : null
          
          const sectionTitle = firstAnswer?.sectionText || 
                              firstAnswer?.sectionTitle || 
                              section?.title ||
                              section?.id
          
          return {
            ...section,
            title: sectionTitle,
            questions: section?.questions?.map((question) => {
              globalQuestionNumber++ // Increment global counter for each question
              const questionId = normalizeId(question?.id)
              const answer = validAnswers?.find((answer) => {
                const answerQuestionId = normalizeId(answer?.questionId)
                return answerQuestionId === questionId && answerQuestionId !== null
              })
              return {
                ...question,
                // Use debug numbering only if requested (for history drawer), otherwise use actual question text
                questionText: useDebugNumbering
                  ? `Question ${globalQuestionNumber}`
                  : (answer?.questionText || question?.questionText),
                shortQuestionText: useDebugNumbering
                  ? `Question ${globalQuestionNumber}`
                  : (answer?.shortQuestionText || question?.shortQuestionText || question?.questionText),
                translations: answer?.translations || question?.translations || {},
                answer,
              }
            }),
          }
        })
      
      if (matchedSections && matchedSections.length > 0) {
        return matchedSections
      }
    }
  }
  
  // If no sections provided OR sections didn't match, reconstruct from answers (for past reports)
  // This ensures we always show questions if answers exist, even if sections don't match
  // Group answers by sectionId if available
  if (validAnswers && validAnswers.length > 0) {
    // Create maps for quick lookup
    // IMPORTANT: Build questionMap from passed sections FIRST, even if they don't match answers
    // This allows us to look up question text from sections when reconstructing
    const questionMap = new Map()
    const sectionMap = new Map() // Map sectionId -> section title
    const questionToSectionMap = new Map() // Map questionId -> section (for finding section from question)
    
    // Always build maps from passed sections if available (even if they don't match answers)
    // This is crucial for history drawer to show question text when questionText is not saved in answers
    if (effectiveSections && Array.isArray(effectiveSections) && effectiveSections.length > 0) {
      effectiveSections.forEach((section) => {
        // Map section by ID for section title lookup (normalize IDs for matching)
        if (section?.id) {
          const normalizedSectionId = normalizeId(section.id)
          if (normalizedSectionId) {
            sectionMap.set(normalizedSectionId, section.title)
          }
        }
        // Map questions by ID for question text lookup and track which section they belong to
        section?.questions?.forEach((question) => {
          const qId = normalizeId(question.id)
          if (qId) {
            questionMap.set(qId, question)
            // Also map question to its section for section title lookup
            questionToSectionMap.set(qId, section)
          }
        })
      })
    }
    
    const answersBySection = {}
    validAnswers.forEach((answer) => {
      const sectionId = answer?.sectionId || 'default'
      if (!answersBySection[sectionId]) {
        answersBySection[sectionId] = []
      }
      answersBySection[sectionId].push(answer)
    })
    
    // DEBUG: Track global question number across all sections
    let globalQuestionNumber = 0
    
    // Create minimal section structure from answers
    const reconstructedSections = Object.keys(answersBySection).map((sectionId, idx) => {
      const sectionAnswers = answersBySection[sectionId]
      
      // Try to find section title from multiple sources:
      // 1. sectionText from answer (new field we're saving)
      // 2. sectionTitle from answer (backward compatibility)
      // 3. From sectionMap using normalized sectionId
      // 4. From questionToSectionMap (find section from first question in this section)
      // 5. Fallback to generic name
      let sectionTitle = sectionAnswers[0]?.sectionText || sectionAnswers[0]?.sectionTitle
      
      if (!sectionTitle) {
        // Try to get from sectionMap using normalized sectionId
        const normalizedSectionId = normalizeId(sectionId)
        sectionTitle = normalizedSectionId ? sectionMap.get(normalizedSectionId) : null
      }
      
      if (!sectionTitle) {
        // Try to get section from first question in this section
        const firstAnswer = sectionAnswers[0]
        if (firstAnswer?.questionId) {
          const normalizedQuestionId = normalizeId(firstAnswer.questionId)
          const sectionFromQuestion = normalizedQuestionId ? questionToSectionMap.get(normalizedQuestionId) : null
          sectionTitle = sectionFromQuestion?.title
        }
      }
      
      // Final fallback: use sectionId if no title found
      if (!sectionTitle) {
        sectionTitle = sectionId || `Screening Questions ${idx + 1}`
      }
      
      return {
        id: sectionId,
        title: sectionTitle,
        questions: sectionAnswers.map((answer, qIdx) => {
          globalQuestionNumber++ // Increment global counter
          const answerQuestionId = normalizeId(answer?.questionId)
          // Try to get question text from sections map if available
          const questionFromSection = answerQuestionId ? questionMap.get(answerQuestionId) : null
          
          return {
            id: answer?.questionId,
            // Priority: 1. questionText from saved answer, 2. questionFromSection, 3. other answer fields, 4. fallback
            questionText: useDebugNumbering
              ? `Question ${globalQuestionNumber}`
              : (answer?.questionText ||  // First priority: questionText saved with answer (not implemented on server yet)
                 questionFromSection?.questionText ||  // Second: from passed sections (CRITICAL for history drawer)
                 questionFromSection?.shortQuestionText ||  // Also try shortQuestionText from sections
                 answer?.question || 
                 answer?.questionLabel ||
                 (answer?.questionId ? `Question ${answer.questionId}` : `Question ${qIdx + 1}`)),
            shortQuestionText: useDebugNumbering
              ? `Question ${globalQuestionNumber}`
              : (answer?.shortQuestionText ||  // First priority: shortQuestionText saved with answer
                 questionFromSection?.shortQuestionText ||  // Second: from passed sections
                 answer?.questionText || 
                 questionFromSection?.questionText ||
                 answer?.question ||
                 answer?.questionLabel ||
                 (answer?.questionId ? answer.questionId : `Question ${qIdx + 1}`)),
            translations: answer?.translations || questionFromSection?.translations || {},
            answer: {
              response: answer?.response,
              additionalResponse: answer?.additionalResponse,
            },
          }
        }),
      }
    })
    
    // Return the reconstructed sections
    if (reconstructedSections && reconstructedSections.length > 0) {
      return reconstructedSections
    }
  }
  
  return []
}

export async function getScreeningSectionsFromDb({ doctorSpecializations, visitType }) {
  const specializations = (doctorSpecializations || []).join(',')

  const response = await getMPDataWithoutParams(
    `/api/secure/screener/screening-section/sections?specializations=${specializations}&visit-type=${visitType}`,
  )

  if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
    const screeningSections = response?.data?.data?.['screening-section']
    return filterOutSectionsWithoutQuestions(screeningSections || [])
  } else {
    toast.error('Error getting screening report data')
    return []
  }
}

function filterOutSectionsWithoutQuestions(sections) {
  return sections?.filter((section) => section?.questions?.length > 0)
}

export function buildEmrSectionData({ patientHistoryEmrData, emrSections }) {
  const sectionHistoryData = []
  if (!patientHistoryEmrData) {
    return null
  }
  let patHistorySectionData = {
    ...patientHistoryEmrData,
  }
  // protected String defCode;              // e.g., "HISTORY" (stable, immutable in defs)
  // protected Integer defVersion;          // e.g., 1
  // protected String sourceDefId;          // e.g., "SEC-HISTORY:v1" (optional)
  // protected String layoutHash;
  if (!patHistorySectionData) {
    return null
  }
  return [patHistorySectionData]
}

export const HARD_CODED_SCREENING_EMR_SECTION_IDS = [
  'patient-history-notes',
  'observations',
  'screening-recommendation',
  'diagnosis',
  'health_complaints',
  'next_visit_details',
  'signature',
  'private_notes',
  'patient-history-notes',
  'free_form_rich_text', // Rendered separately with TipTap editor
]
